import { FC } from 'react';
import { css } from '@emotion/react';
import { mapKeys, camelCase } from 'lodash';
import { colors, spacing } from '../../../styles';
import { TimeRange } from '../../../types';
import { InvestmentTable, ColumnSelection, ColumnSelectionType } from '../../InvestmentTable';
import { CollectionInvestment } from '../../../apis/backend';
import { ToolRendererProps } from './types';
import { LoadingState } from './shared/LoadingState';

// Shared helper to transform investment data for InvestmentTable
const transformInvestmentData = (investment: CollectionInvestment): any => {
  const transformed = mapKeys(investment, (_value, key) => camelCase(key));
  // Fix field name mismatch: backend returns "price" but InvestmentTable expects "latestPrice"
  if (transformed.price && !transformed.latestPrice) {
    transformed.latestPrice = transformed.price;
  }
  return transformed;
};

// Collection Stocks Renderer
export const CollectionStocksRenderer: FC<ToolRendererProps> = ({ output, isLoading }) => {
  if (isLoading) {
    return <LoadingState message="Loading collection stocks..." />;
  }

  // Type-safe extraction of collection data from the API response
  const rawInvestments = output.investments as CollectionInvestment[] | undefined;
  const collectionName = output.name;
  const collectionSection = output.section;
  const collectionUpdated = output.updated;

  if (!rawInvestments || (Array.isArray(rawInvestments) && rawInvestments.length === 0)) {
    return (
      <div
        css={css`
          font-size: 14px;
          color: ${colors.silver};
        `}
      >
        No stocks found in collection
      </div>
    );
  }

  // Transform snake_case keys to camelCase for InvestmentTable compatibility
  const investments = rawInvestments.map(transformInvestmentData);

  // Configure column selections for collection display
  const columnSelections: ColumnSelection[] = [
    { type: ColumnSelectionType.Name },
    { type: ColumnSelectionType.BottomLine },
    { type: ColumnSelectionType.LatestPrice },
    { type: ColumnSelectionType.ChangePercent, timeRange: TimeRange.days1 },
    { type: ColumnSelectionType.MarketCap },
  ];

  return (
    <div
      css={css`
        margin: ${spacing.centi} 0 0 0;
      `}
    >
      {/* Collection Header */}
      {collectionName && (
        <div
          css={css`
            margin-bottom: ${spacing.deci};
          `}
        >
          <div
            css={css`
              font-weight: 600;
              font-size: 16px;
              color: ${colors.petrolBlue};
              margin-bottom: ${spacing.milli};

              html[data-theme='dark'] & {
                color: ${colors.skyBlue};
              }
            `}
          >
            {collectionName}
          </div>
          <div
            css={css`
              font-size: 12px;
              color: ${colors.silver};
              display: flex;
              gap: ${spacing.centi};
              align-items: center;
            `}
          >
            {collectionSection && (
              <span
                css={css`
                  background: ${colors.petrolBlue};
                  color: white;
                  padding: ${spacing.micro} 6px;
                  border-radius: 3px;
                  font-size: 10px;
                  text-transform: uppercase;
                  font-weight: 500;

                  html[data-theme='dark'] & {
                    background: ${colors.skyBlue};
                    color: ${colors.liquorice};
                  }
                `}
              >
                {collectionSection}
              </span>
            )}
            {collectionUpdated && <span>Updated {collectionUpdated}</span>}
            <span>
              {investments.length} stock{investments.length === 1 ? '' : 's'}
            </span>
          </div>
        </div>
      )}

      {/* Investment Table */}
      <InvestmentTable
        investments={investments}
        columnSelections={columnSelections}
        sortingRule={{ id: 'marketCap', desc: true }}
        hideColumnControls={true}
        showBookmark={false}
        isLoading={false}
      />
    </div>
  );
};
